<template>
	<section class="wrapper" ref="root">
		<div class="interactive-hero">
			<a class="interactive-hero__skiplink"
				tabindex="0"
				@keydown.enter.prevent.stop="skipSection()"
				@click.prevent.stop="skipSection()"
			>
				Skip Section
			</a>

			<div :class="['interactive-hero__loading-wrapper', { 'is-active': isLoading }]">
				<div class="interactive-hero__loading-spinner"></div>
			</div>

			<div class="interactive-hero__wrapper">
				<img class="interactive-hero__image" :src="headerImageUrl" alt="">

				<div class="interactive-hero__right">
					<div class="interactive-hero__top" :style="{ backgroundImage: headerBackgroundImage }">
						<StepTitle class="interactive-hero__step-one" text="Welcome to Live Yes! Connect Groups" :is-disabled="false" />

						<div class="interactive-hero__row">
							<button class="interactive-hero__button"
								:tabindex="activeStep ? '-1' : '0'"
								@click.stop.prevent="handleSelectGroupType('locations')"
								@keydown.enter.prevent="handleSelectGroupType('locations')"
							>
								<span class="interactive-hero__label">Select a State</span>

								<ArrowDown class="interactive-hero__arrow"/>
							</button>

							<p class="interactive-hero__separator">OR</p>

							<button class="interactive-hero__button"
								:tabindex="activeStep ? '-1' : '0'"
								@click.stop.prevent="handleSelectGroupType('topics')"
								@keydown.enter.prevent="handleSelectGroupType('topics')"
							>
								<span class="interactive-hero__label">Select by Topic</span>

								<ArrowDown class="interactive-hero__arrow" />
							</button>
						</div>

						<GroupsDrawer class="interactive-hero__drawer"
							:topics-list="topicsList"
							:locations-list="locationsList"
							:active-group="activeGroup"
							:active-group-type="activeGroupType"
							:active-step="activeStep"
							@select-group="group => handleSelectGroup(group)"
							@go-to-previous-step="goToPreviousStep()"
							@cancel="handleCancel()"
						/>
					</div>

					<div class="interactive-hero__bottom">
						<StepTitle number="2" text="Find Your Group" :is-disabled="!activeGroupType" />
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
	import axios from 'axios';
	import locationsList from '@/constants/locations.json';
	import topicsList from '@/constants/topics.json';

	import ArrowDown from '@/components/ArrowDown.vue';
	import CircleNumber from '@/components/CircleNumber.vue';
	import GroupsDrawer from '@/components/GroupsDrawer.vue';
	import StepTitle from '@/components/StepTitle.vue';

	export default {
		name: 'App',

		components: {
			ArrowDown,
			CircleNumber,
			GroupsDrawer,
			StepTitle
		},

		created() {
			this.watchResize();
			window.addEventListener('resize', this.watchResize);

			//API call
			this.getGroupLists();

			// Mocking API call
			// this.getGroupLists()
			// 	.then(() => {
			// 		this.locationsList = locationsList.locations;
			// 		this.topicsList = topicsList.topics;
			// 	})

			
		},

		destroyed() {
			window.removeEventListener('resize', this.watchResize);
		},

		props: {
			message: String,
			headerImageUrl: String,
			skiplinkTargetElement: String,
			apiEndpoint: String,
		},

		data () {
			return {
				isDesktopScreen: true,
				activeStep: null,
				activeGroup: null,
				activeGroupType: null, // 'state' or 'topic'
				locationsList: [],
				topicsList: [],
			};
		},

		watch: {
			activeStep(activeStep) {
				const isDrawerOpen = !!activeStep;
				const root = this.$refs.root;

				const closeDrawerIfClickOutsideComponent = event => { !root.contains(event.target) && this.handleCancel() }

				if (isDrawerOpen) {
					document.addEventListener('click', closeDrawerIfClickOutsideComponent);

					return;
				}

				document.removeEventListener('click', closeDrawerIfClickOutsideComponent);
			},
		},

		computed: {
			isLoading() {
				const isLocationsListPopulated = this.locationsList && this.locationsList.length;
				const isTopicsListPopulated = this.topicsList && this.topicsList.length;

				return !isLocationsListPopulated || !isTopicsListPopulated;
			},

			headerBackgroundImage() {
				const opaqueBackgroundImage = 'linear-gradient(rgba(50, 98, 149, 0.9), rgba(50, 98, 149, 0.9)), url(' + this.headerImageUrl + ')';

				return this.isDesktopScreen
					? ''
					: opaqueBackgroundImage;
			}
		},

		methods: {
			getGroupLists() {
				return axios(this.apiEndpoint)
					.then(result => {

						if (!result || !result.data) return console.log('could not retrieve groups');
						this.topicsList = (result && result.data.topics) || [];
						this.locationsList = (result && result.data.locations) || [];
					})
					.catch(error => {
						console.log('error', error);
					});
			},

			skipSection() {
				const targetClass = this.skiplinkTargetElement || 'main';
				const targetElement = document.querySelector(targetClass);

				if (!targetClass || !targetElement) return;

				const topOffset = targetElement.getBoundingClientRect().top + window.scrollY;

				window.scrollTo({ top: topOffset, behavior: 'smooth' });
				targetElement.focus();
			},

			handleSelectGroupType(type) {
				this.activeGroupType = type;

				this.activeStep = type === 'locations'
					? 1
					: 2;
			},

			handleSelectGroup(group) {
				this.activeGroup = group;
				this.activeStep = 2;
			},

			handleCancel() {
				this.activeStep = null;
				this.activeGroup = null;
				this.activeGroupType = null;
			},

			watchResize() {
				if (window.innerWidth > 768) this.isDesktopScreen = true;
				else this.isDesktopScreen = false;
			},

			goToPreviousStep() {
				this.activeStep = 1;
				this.activeGroup = null;
			},
		},
	}
</script>

<style lang="scss" scoped>
	@import '@/styles/variables';
	@import '@/styles/mixins';

	$height-desktop: 403px;
	$height-mobile: 355px;

	@keyframes rotate {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}

	.interactive-hero {
		position: relative;
		height: $height-desktop;

		&__skiplink {
			position: absolute;
			top: 0;
			left: 0;
			background: $lightGray;
			font-weight: bold;
			padding: 12px 16px;
			z-index: 3;
			opacity: 0;
			transition: opacity 0.25s linear;
			pointer-events: none;

			&:focus {
				border: 1px solid blue;
				opacity: 1;
				pointer-events: all;
			}
		}

		&__loading-wrapper {
			position: absolute;
			display: flex;
			justify-content: center;
			align-items: center;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			background-color: rgba(0, 0, 0, 0.3);
			z-index: 2;
			opacity: 0;
			pointer-events: none;

			&.is-active {
				opacity: 1;
				pointer-events: all;
				cursor: not-allowed;
			}
		}

		&__loading-spinner {
			height: 80px;
			width: 80px;
			border-top: 8px solid $darkBlue;
			border-left: 8px solid $white;
			border-right: 8px solid $white;
			border-bottom: 8px solid $darkBlue;
			border-radius: 100%;
			animation: rotate 2s infinite ease;
		}

		&__wrapper {
			display: grid;
			grid-template-columns: 33.3% 1fr;
			height: 100%;
		}

		&__image {
			display: block;
			height: 100%;
			width: 100%;
			object-fit: cover;
		}

		&__right {
			position: relative;
			display: flex;
			flex-direction: column;
		}

		&__top {
			flex-grow: 2;
			background-color: $lightBlue;
			padding: 64px 32px;
		}

		&__button {
			@include heading-01();
			position: relative;
			display: flex;
			align-items: center;
			padding: 0;
			appearance: none;
			border: none;
			background-color: transparent;
			cursor: pointer;

			&:after {
				@include dotted-line-animation();
			}

			&:hover:after,
			&:focus:after {
				animation-play-state: running;
			}
		}

		&__label {
			padding-bottom: 8px;
			margin-right: 18px;
		}

		&__arrow {
			width: 18px;
			margin-top: -5px; // to offset for weird svg dimensions
		}

		&__separator {
			color: white;
			font-size: 20px;
			margin-left: 24px;
			margin-right: 24px;
		}

		&__drawer {
			box-shadow: 0 0 15px 2px rgba(0, 0, 0, 0.15);
		}

		&__bottom {
			@include background-polka-dot();
			padding: 40px 32px;
		}

		&__row {
			display: flex;
			align-items: center;

			&:not(:first-of-type) {
				margin-left: calc(40px + 20px); // number width + margin
			}
		}

		&__step-one {
			margin-bottom: 24px;
		}
	}

	@media(max-width: $screen-md) {
		.interactive-hero {
			height: $height-mobile;

			&__image {
				display: none;
			}

			&__wrapper {
				grid-template-columns: unset;
			}

			&__top {
				background-position: 50% 50%;
				padding: 32px 32px 64px;
			}

			&__bottom {
				padding: 24px;
			}

			&__row {
				&:not(:first-of-type) {
					margin: 0;
					flex-direction: column;
				}
			}

			&__step-one {
				margin-bottom: 32px;
			}

			&__separator {
				margin: 18px 0;
			}
		}
	}
</style>
